import { trackClick } from "@/lib/membership";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, SlidersHorizontal, Newspaper, Plus, Loader2, Compass } from "lucide-react";
import { getForYou, getCategories } from "@/lib/api";
import { displayCat } from "@/lib/readoo";
import { CategoryChips } from "@/components/CategoryChips";
import { WeatherStrip } from "@/components/WeatherStrip";
import { LocalNewsBox } from "@/components/LocalNewsBox";
import { NewsCard, HeroCard } from "@/components/NewsCard";
import { CategoryTopStory } from "@/components/TopStories";
import { CardSkeleton, HeroSkeleton, EmptyState } from "@/components/Skeletons";
import { MySignCard } from "@/components/MySignCard";
import { SEO } from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 16;

export default function ForYou() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState([]);

  useEffect(() => { document.title = "Per te · Readoo"; }, []);
  useEffect(() => { getCategories().then((r) => setCategories(r.categories || [])).catch(() => {}); }, []);
  useEffect(() => { if (!authLoading && !user) navigate("/login", { replace: true }); }, [authLoading, user, navigate]);

  if (authLoading || !user) return null;

  const hasInterests = ((user.interests?.length || 0) + (user.interest_tags?.length || 0)) > 0 || !!user.zodiac_sign;

  return (
    <div>
      <SEO title="Per te" description="Il tuo feed personalizzato di Readoo: solo le notizie delle categorie che segui." />
      <CategoryChips
        categories={categories}
        active="__foryou"
        showForYou={hasInterests}
        forYouActive={hasInterests}
        showCreateFeed={!hasInterests}
      />
      <ForYouFeed />
    </div>
  );
}

function ForYouFeed() {
  const [interests, setInterests] = useState([]);
  const [tags, setTags] = useState([]);
  const [showHoroscope, setShowHoroscope] = useState(false);
  const [hasInterests, setHasInterests] = useState(true);
  const [focus, setFocus] = useState(null);  // null = "Tutti"; altrimenti categoria o slug squadra
  const [items, setItems] = useState([]);
  const [top, setTop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pagesRef = useRef(1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    pagesRef.current = 1;
    getForYou({ limit: PAGE_SIZE, skip: 0, focus })
      .then((r) => {
        if (!alive) return;
        setInterests(r.categories || []);
        setTags(r.tags || []);
        setShowHoroscope(!!r.show_horoscope);
        setHasInterests(!!r.has_interests);
        const list = r.articles || [];
        setTop(r.top || null);
        setItems(list);
        setHasMore(list.length >= PAGE_SIZE);
      })
      .catch(() => { if (alive) { setItems([]); setTop(null); setHasInterests(true); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [focus]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const skip = pagesRef.current * PAGE_SIZE;
      const r = await getForYou({ limit: PAGE_SIZE, skip, focus });
      const list = r.articles || [];
      pagesRef.current += 1;
      setItems((prev) => {
        const seen = new Set(prev.map((a) => a.article_id));
        return [...prev, ...list.filter((a) => !seen.has(a.article_id))];
      });
      setHasMore(list.length >= PAGE_SIZE);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && interests.length === 0 && tags.length === 0) {
    return (
      <div className="max-w-[880px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <HeroSkeleton />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      </div>
    );
  }

  // CASO B — utente loggato senza alcun interesse (né categorie né squadre): CTA
  if (!hasInterests) {
    return (
      <div className="max-w-[880px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div data-testid="foryou-empty-interests" className="rounded-2xl border border-brand/40 bg-brandTertiary/40 p-8 sm:p-10 text-center">
          <Sparkles size={30} className="text-brand mx-auto mb-4" />
          <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-onSurface">Crea il tuo feed</h1>
          <p className="text-sm text-subtle mt-2 max-w-md mx-auto">
            Scegli le categorie che ti interessano e Readoo comporrà un feed su misura per te, con le notizie che contano davvero.
          </p>
          <Link
            to="/profilo#interessi"
            data-testid="foryou-configure-cta"
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"
          >
            <SlidersHorizontal size={16} /> Personalizza Readoo
          </Link>
        </div>
      </div>
    );
  }

  const listed = top ? items.filter((a) => a.article_id !== top.article_id && (!top.cluster_key || a.cluster_key !== top.cluster_key)) : items;
  const [hero, ...rest] = listed;
  const teamLabel = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const summaryParts = [...interests.map(displayCat), ...tags.map(teamLabel)];

  return (
    <div className="max-w-[880px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <WeatherStrip />
      <LocalNewsBox className="mb-6" testid="local-news-foryou" />
      {/* Header "Per te" con interessi seguiti + link gestione */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-brand" />
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-onSurface">Per te</h1>
          </div>
          <Link
            to="/profilo#interessi"
            data-testid="foryou-manage-interests"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-hair bg-surfaceSecondary text-sm font-medium text-onSurfaceSecondary hover:text-brand hover:border-brand/50 transition-colors"
          >
            <SlidersHorizontal size={15} /> Gestisci interessi
          </Link>
        </div>
        {summaryParts.length > 0 ? (
          <div data-testid="foryou-interest-filters" className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-subtle mr-1">I tuoi interessi:</span>
            <button
              type="button"
              onClick={() => setFocus(null)}
              data-testid="foryou-filter-all"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${focus === null ? "bg-brand text-onBrand border-brand" : "bg-surfaceSecondary text-onSurfaceSecondary border-hair hover:border-brand/50 hover:text-brand"}`}
            >
              Tutti
            </button>
            {interests.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFocus(c)}
                data-testid={`foryou-filter-${c}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${focus === c ? "bg-brand text-onBrand border-brand" : "bg-surfaceSecondary text-onSurfaceSecondary border-hair hover:border-brand/50 hover:text-brand"}`}
              >
                {displayCat(c)}
              </button>
            ))}
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setFocus(t)}
                data-testid={`foryou-filter-${t}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${focus === t ? "bg-brand text-onBrand border-brand" : "bg-surfaceSecondary text-onSurfaceSecondary border-hair hover:border-brand/50 hover:text-brand"}`}
              >
                {teamLabel(t)}
              </button>
            ))}
          </div>
        ) : (
          <p data-testid="foryou-interests-summary" className="text-sm text-subtle mt-3">
            Il tuo oroscopo del giorno, scelto in base al tuo segno.
          </p>
        )}
      </div>

      {/* Oroscopo tra gli interessi → blocco "Il mio segno" (non articoli) */}
      {showHoroscope && (
        <div data-testid="foryou-mysign" className="mb-6">
          <MySignCard />
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      ) : items.length === 0 ? (
        showHoroscope ? null : (
          // CASO stato vuoto — interessi selezionati ma nessun articolo
          <EmptyState
            icon={Newspaper}
            title="Non ci sono ancora nuove notizie negli interessi che segui"
            subtitle="Torna più tardi oppure esplora tutte le notizie di Readoo."
            action={
              <Link to="/" data-testid="foryou-discover-all" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors">
                <Compass size={15} /> Scopri tutte le notizie
              </Link>
            }
          />
        )
      ) : (
        <>
          <div className="space-y-5">
            <CategoryTopStory article={top} testid="foryou-top-story" />
            {hero && <HeroCard article={hero} />}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {rest.map((a, i) => {
                  const wide = rest.length % 2 === 1 && i === rest.length - 1;
                  return (
                    <div key={a.article_id} className={wide ? "sm:col-span-2" : ""} onClickCapture={() => trackClick("personalized_feed")}>
                      <NewsCard article={a} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="foryou-load-more"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-hair bg-surfaceSecondary text-onSurface hover:border-brand/50 hover:text-brand disabled:opacity-60 transition-colors text-sm font-semibold"
              >
                {loadingMore ? <><Loader2 size={16} className="animate-spin" /> Caricamento…</> : <><Plus size={16} /> Più articoli</>}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
